import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MRT_ColumnFiltersState } from 'material-react-table';
import { TableState, PaginationOptions } from 'store/userOrg/injectedUserOrgApi';

export enum TableKey {
    Datasets = 'datasets',
    Projects = 'projects',
    Workflows = 'workflows',
    Locations = 'locations',
    Providers = 'providers',
    ResourceManagement = 'resourceManagement',
    UploadTransferStatuses = 'uploadTransferStatuses',
    StageTransferStatuses = 'stageTransferStatuses',
    DeleteTransferStatuses = 'deleteTransferStatuses',
    JobScriptWorkflows = 'jobScriptWorkflows',
    Jobscripts = 'jobscripts',
    JobscriptVersions = 'jobscriptVersions',
    Containers = 'containers',
}

export type TableStateExtended = TableState & {
    searchError?: string;
    selectedRowIds?: string[];
    columnSearch?: MRT_ColumnFiltersState;
};

export type TableOptionsExtended = Record<TableKey, TableStateExtended>;

const persistTableOptions = (state: TableOptionsExtended) => {
    localStorage.setItem('tableOptions', JSON.stringify(state));
};

const getInitialState = (): TableOptionsExtended => {
    const defaultDefaults: TableStateExtended = { searchText: '', pagination: { pageIndex: 0, pageSize: 10 } };
    const smallDefaults: TableStateExtended = { searchText: '', pagination: { pageIndex: 0, pageSize: 5 } };
    
    let stored: Partial<Record<TableKey, TableStateExtended>> = {};
    try {
        const storedItem = localStorage.getItem('tableOptions');
        if (storedItem) stored = JSON.parse(storedItem);
    } catch (e) {
        console.error('Error parsing tableOptions from localStorage', e);
    }

    const state = {} as TableOptionsExtended;
    
    Object.values(TableKey).forEach((key) => {
        const isSmall = [TableKey.JobScriptWorkflows, TableKey.Jobscripts, TableKey.JobscriptVersions].includes(key);
        const baseDefaults = isSmall ? smallDefaults : defaultDefaults;
        
        state[key] = stored[key] ?? { ...baseDefaults };
        
        if ([TableKey.Datasets, TableKey.Jobscripts, TableKey.Containers].includes(key)) {
            state[key].selectedRowIds = state[key].selectedRowIds ?? [];
        }
    });

    return state;
};

const tableOptionsSlice = createSlice({
    name: 'tableOptions',
    initialState: getInitialState(),
    reducers: {
        setTablePagination: (
            state,
            action: PayloadAction<{ table: TableKey; pagination: PaginationOptions }>
        ) => {
            state[action.payload.table].pagination = action.payload.pagination;
            persistTableOptions(state);
        },
        setTableSearchText: (
            state,
            action: PayloadAction<{ table: TableKey; searchText: string | undefined }>
        ) => {
            state[action.payload.table].searchText = action.payload.searchText;
            persistTableOptions(state);
        },
        setTableSearchError: (
            state,
            action: PayloadAction<{ table: TableKey; error: string | undefined }>
        ) => {
            state[action.payload.table].searchError = action.payload.error;
            persistTableOptions(state);
        },
        setTableColumnSearch: (
            state,
            action: PayloadAction<{ table: TableKey; columnSearch: MRT_ColumnFiltersState | undefined }>
        ) => {
            state[action.payload.table].columnSearch = action.payload.columnSearch;
            persistTableOptions(state);
        },
        setTableSelectedRowIds: (
            state,
            action: PayloadAction<{ table: TableKey; selectedRowIds: string[] }>
        ) => {
            state[action.payload.table].selectedRowIds = action.payload.selectedRowIds;
            persistTableOptions(state);
        },
        resetTableSelectedRowIds: (state, action: PayloadAction<TableKey>) => {
            state[action.payload].selectedRowIds = [];
            persistTableOptions(state);
        },
    },
});

export const {
    setTablePagination,
    setTableSearchText,
    setTableSearchError,
    setTableColumnSearch,
    setTableSelectedRowIds,
    resetTableSelectedRowIds,
} = tableOptionsSlice.actions;

export default tableOptionsSlice.reducer;
